import { FC, useMemo } from 'react'

import InputItem from '../../../components/InputItem'
import { sortDropdown } from '../../../shared/formFunctions'
import { IEndOfLifeConfiguration } from '../types'
import { getProductOptions, isSingleProductType } from '../utils'

interface ICountryInfo {
  formik: any
  configuration: IEndOfLifeConfiguration
  code: string | null
  onTaxonomyLevelChange(e: React.ChangeEvent<Element>): void
}

export const CountryInfo: FC<ICountryInfo> = ({
  formik,
  configuration,
  code,
  onTaxonomyLevelChange,
}) => {
  // Configuration on demand: the product list is resolved from the selected taxonomy level rather
  // than fetched per level. Both lists arrive on the End of Life configuration itself.
  const productOptions = useMemo(
    () => getProductOptions(configuration, Number(formik.values.productTaxonomyLevel) || null),
    [configuration, formik.values.productTaxonomyLevel]
  )

  // One product type today, so the field is read-only. A second member makes it a dropdown with no
  // change here beyond this flag.
  const productTypeIsFixed = isSingleProductType(configuration)
  const productTypeLabel = configuration.productType?.[0]?.description ?? ''

  return (
    <div className="piece-content-inputs mb-5">
      <InputItem
        // Deterministic reset key: remount only when the selected country actually changes.
        key={formik.values.countryId ?? -1}
        name="Country Name"
        description="The country this End of Life record applies to."
        type="select"
        onChange={formik.handleChange}
        formikName="countryId"
        create={true}
        showAsterisk
        formik={formik}
        defaultValue={formik.values.countryId ?? -1}
        options={sortDropdown(
          configuration.countries,
          formik.values.countryId ?? -1,
          'countryName'
        )}
      />
      <InputItem
        name="Country ISO Code"
        description="The same country, selected by its ISO code."
        type="select"
        create={true}
        onChange={formik.handleChange}
        formikName="countryId"
        formik={formik}
        defaultValue={formik.values.countryId ?? -1}
        options={sortDropdown(configuration.countries, formik.values.countryId ?? -1, 'countryISO')}
      />
      <InputItem
        name="Region Identifier"
        description="Location region for this country. Required to publish."
        type="select"
        onChange={formik.handleChange}
        formikName="regionIdentifierId"
        formik={formik}
        defaultValue={formik.values.regionIdentifierId ?? -1}
        options={sortDropdown(
          configuration.regions,
          formik.values.regionIdentifierId ?? -1,
          'name'
        )}
      />
      {productTypeIsFixed ? (
        <InputItem
          disabled
          name="Product type"
          description="Fixed while End of Life covers packaging only, and submitted with the record."
          type="input"
          onChange={formik.handleChange}
          formikName="productTypeDisplay"
          formik={formik}
          defaultValue={productTypeLabel}
        />
      ) : (
        <InputItem
          name="Product type"
          description="The domain whose taxonomy supplies the product below."
          type="select"
          onChange={formik.handleChange}
          formikName="productType"
          showAsterisk
          formik={formik}
          defaultValue={formik.values.productType ?? -1}
          options={sortDropdown(
            configuration.productType,
            formik.values.productType ?? -1,
            'description'
          )}
        />
      )}
      <InputItem
        name="Taxonomy level"
        description="Which level of the taxonomy this record's recycling rate is held at."
        type="select"
        onChange={onTaxonomyLevelChange}
        formikName="productTaxonomyLevel"
        showAsterisk
        formik={formik}
        defaultValue={formik.values.productTaxonomyLevel ?? -1}
        options={sortDropdown(
          configuration.productTaxonomyLevel,
          formik.values.productTaxonomyLevel ?? -1,
          'description'
        )}
      />
      <InputItem
        disabled={!formik.values.productTaxonomyLevel}
        name="Product"
        description="Select a taxonomy level first; this list is the published taxonomy at that level."
        type="select"
        onChange={formik.handleChange}
        formikName="productId"
        showAsterisk
        formik={formik}
        defaultValue={formik.values.productId ?? -1}
        options={sortDropdown(productOptions, formik.values.productId ?? -1, 'name')}
      />
      <InputItem
        disabled
        name="External code"
        description="Generated on save."
        type="input"
        onChange={formik.handleChange}
        formikName="code"
        formik={formik}
        defaultValue={code ?? ''}
      />
    </div>
  )
}
